import React, { useEffect, useMemo, useState } from 'react';
import { RiPlayFill, RiLockLine, RiSparkling2Line, RiArrowLeftSLine, RiArrowRightSLine } from 'react-icons/ri';
import styles from './StoryTimeHero.module.css';

const ROTATE_MS = 8000;
const MAX_SLIDES = 3;

// Newest first. Promotion writes no date into the manifest, so fall back to
// the job id order the shelf already shows.
const byNewest = (a, b) => (Date.parse(b?.createdAt || '') || 0) - (Date.parse(a?.createdAt || '') || 0);

// Build the carousel from what the listener has not heard yet. One premium
// slide, one free slide, and a second premium slide when there is one; the
// first card depends on where the listener is: a subscriber sees the newest
// premium series first, a free account sees the premium pitch first too
// (episode one is the taste), and a brand-new account with nothing played
// sees a free series so the first tap cannot hit a lock.
function pickSlides(premiumBooks, freeBooks, subscriptionActive) {
  const unheard = (books) => (books || []).filter((book) => book?.playbackState !== 'read').sort(byNewest);
  const premium = unheard(premiumBooks);
  const free = unheard(freeBooks);
  const anyPlayed = [...(premiumBooks || []), ...(freeBooks || [])].some((book) => book?.playbackState && book.playbackState !== 'new');

  const slides = [];
  const push = (book, kind) => {
    if (book && !slides.some((slide) => slide.book.id === book.id)) slides.push({ book, kind });
  };
  if (!anyPlayed && !subscriptionActive) {
    push(free[0], 'free');
    push(premium[0], 'premium');
    push(free[1], 'free');
  } else {
    push(premium[0], 'premium');
    push(free[0], 'free');
    push(premium[1], 'premium');
  }
  return slides.slice(0, MAX_SLIDES);
}

function StoryTimeHero({
  loading,
  premiumBooks,
  freeBooks,
  subscriptionActive,
  openingKey,
  onOpenBook,
  onMakeYourOwn,
}) {
  const slides = useMemo(
    () => pickSlides(premiumBooks, freeBooks, subscriptionActive),
    [premiumBooks, freeBooks, subscriptionActive]
  );
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (index >= slides.length) setIndex(0);
  }, [slides.length, index]);

  useEffect(() => {
    if (paused || slides.length < 2) return undefined;
    const reduced = typeof window !== 'undefined'
      && window.matchMedia
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduced) return undefined;
    const timer = window.setInterval(() => setIndex((i) => (i + 1) % slides.length), ROTATE_MS);
    return () => window.clearInterval(timer);
  }, [paused, slides.length]);

  if (loading) {
    return <section className={`${styles.hero} ${styles.skeleton}`} aria-busy="true" aria-label="Loading Story Time" />;
  }
  if (slides.length === 0) return null;

  const slide = slides[Math.min(index, slides.length - 1)];
  const { book, kind } = slide;
  const episodes = Array.isArray(book.chapters) ? book.chapters.length : 0;
  const isPremium = kind === 'premium';
  const inProgress = book.playbackState === 'in_progress';
  const isOpening = openingKey === `c:${book.id}`;
  const eyebrow = isPremium
    ? (subscriptionActive ? 'Story Time Premium' : 'Story Time Premium · Episode 1 free')
    : 'Story Time Originals · Free for everyone';
  const cta = inProgress ? 'Continue' : (isPremium && !subscriptionActive ? 'Play episode 1 free' : 'Play episode 1');
  const title = String(book.title || book.name || 'Untitled series');
  const description = String(book.description || book.logline || '').trim();
  const genre = String(book.genre || '').trim();
  const poster = book.coverUrl || '';

  return (
    <section
      className={`${styles.hero} ${isPremium ? styles.heroPremium : ''}`}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
      aria-roledescription="carousel"
      aria-label="Story Time"
      data-tour="story-time-hero"
    >
      {poster && <div className={styles.backdrop} style={{ backgroundImage: `url("${poster}")` }} aria-hidden="true" />}
      <div className={styles.scrim} aria-hidden="true" />

      <div className={styles.inner} key={book.id}>
        <button
          type="button"
          className={styles.posterButton}
          onClick={() => onOpenBook(book)}
          disabled={isOpening}
          aria-label={`${cta}: ${title}`}
        >
          {poster ? (
            <img className={styles.poster} src={poster} alt="" loading="eager" />
          ) : (
            <div className={styles.posterFallback} aria-hidden="true"><RiSparkling2Line size={28} /></div>
          )}
        </button>

        <div className={styles.body}>
          <span className={styles.eyebrow}>
            {isPremium && <RiLockLine size={12} aria-hidden="true" />}
            {eyebrow}
          </span>
          <h2 className={styles.title}>{title}</h2>
          {description && <p className={styles.logline}>{description}</p>}
          <div className={styles.meta}>
            {genre && <span className={styles.chip}>{genre}</span>}
            {episodes > 0 && <span className={styles.chip}>{episodes} episodes</span>}
            <span className={styles.chip}>Full cast</span>
          </div>
          <div className={styles.actions}>
            <button
              type="button"
              className={styles.play}
              onClick={() => onOpenBook(book)}
              disabled={isOpening}
            >
              {isOpening ? <span className={styles.spinner} aria-hidden="true" /> : <RiPlayFill size={18} />}
              {isOpening ? 'Opening…' : cta}
            </button>
            {onMakeYourOwn && (
              <button type="button" className={styles.secondary} onClick={onMakeYourOwn}>
                <RiSparkling2Line size={16} />
                Make your own
              </button>
            )}
          </div>
        </div>
      </div>

      {slides.length > 1 && (
        <div className={styles.nav}>
          <button
            type="button"
            className={styles.navButton}
            onClick={() => setIndex((i) => (i - 1 + slides.length) % slides.length)}
            aria-label="Previous series"
          >
            <RiArrowLeftSLine size={18} />
          </button>
          <div className={styles.dots} role="tablist" aria-label="Featured series">
            {slides.map((item, i) => (
              <button
                key={item.book.id}
                type="button"
                role="tab"
                aria-selected={i === index}
                className={`${styles.dot} ${i === index ? styles.dotActive : ''}`}
                onClick={() => setIndex(i)}
                aria-label={`Show ${item.book.title || 'series'} ${i + 1} of ${slides.length}`}
              />
            ))}
          </div>
          <button
            type="button"
            className={styles.navButton}
            onClick={() => setIndex((i) => (i + 1) % slides.length)}
            aria-label="Next series"
          >
            <RiArrowRightSLine size={18} />
          </button>
        </div>
      )}
    </section>
  );
}

export default StoryTimeHero;
